import { IconButton, MenuItem, Typography } from '@mui/material';
import Menu from '@mui/material/Menu';
import type { GetLightInvoicesResponse } from '@weft/api-definition';
import { VyIcon } from '@weft/icons';
import { getPrimaryContact } from '@weft/services';
import { INTEGRATION_PROVIDERS, INVOICE_REVIEW_STATUSES } from '@weft/types';
import type { ItemOf } from '@weft/utils';
import { isEmptyArray, isNotEmptyArray, isNotNullOrUndefined, isNullOrUndefined } from '@weft/utils';
import { runMutation } from 'apps/app/src/utils/vy-mutation';
import React, { useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { useConnectedIntegrations } from '../../../../hooks/requests/use-integrations';
import { useInvoicePdf } from '../../../../hooks/requests/use-invoice-pdf';
import { useSyncToStripe } from '../../../../hooks/requests/use-sync-stripe';
import { useActivate } from '../../../../hooks/use-activate';
import { useDownloadInvoicePdf } from '../../../../hooks/use-download-invoice-pdf';
import { useMenu } from '../../../../hooks/use-menu';
import { ShareInvoicesDialog } from '../../invoices-components/share-invoices-dialog/ShareInvoicesDialog';
import { LightInvoicesPdfDownloader } from '../light-invoices-pdf-downloader/LightInvoicesPdfDownloader';

type LightInvoiceViewPageActionsMenuProps = {
  invoiceId: string;
  invoice?: ItemOf<GetLightInvoicesResponse['invoices']>;
};

export const LightInvoiceViewPageActionsMenu: React.FC<LightInvoiceViewPageActionsMenuProps> = ({ invoiceId, invoice }) => {
  const { isOpen, handleMenuOpen, handleMenuClose, anchorElement } = useMenu();
  const { data: connectedIntegrations = [] } = useConnectedIntegrations({ suspense: true });
  const syncInvoicesToStripeMutation = useSyncToStripe();
  const [isDownloadingPdfStatement, setDownloadingAsPdfStatement, unsetDownloadingAsPdfStatement] = useActivate(false);
  const queryClient = useQueryClient();
  const { data: invoicePdf } = useInvoicePdf(invoiceId, { enabled: invoice?.reviewStatus === INVOICE_REVIEW_STATUSES.Approved });

  const handleDownloadPdf = useDownloadInvoicePdf(invoicePdf?.encodedPdf, invoice?.id, invoice?.customer.name, invoice?.billingCycle);

  const handleSyncToStripe = useCallback(async () => {
    await runMutation(
      syncInvoicesToStripeMutation,
      { invoiceIds: [invoiceId] },
      {
        pending: 'Syncing invoice to stripe',
        success: 'Invoice synced to stripe',
        error: 'Failed to sync invoice to stripe',
      },
      {
        onSuccess: () => queryClient.cancelQueries({
          predicate: query => query.queryKey.includes(invoiceId),
        }),
      },
    );
  }, [syncInvoicesToStripeMutation, invoiceId, queryClient]);

  const handleMenuItemClick = (action?: () => void) => {
    return () => {
      handleMenuClose();
      action?.();
    };
  };

  return (
    <>
      <IconButton onClick={handleMenuOpen}>
        <VyIcon
          icon="DotsMenu"
          color="#323232"
        />
      </IconButton>

      <Menu
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={isOpen}
        anchorEl={anchorElement}
        onClose={handleMenuClose}
        MenuListProps={{
          className: 'py-2',
        }}
        slotProps={{
          paper: {
            className: 'mt-2 max-w-[250px] w-full rounded-lg',
          },
        }}
      >
        <MenuItem
          className='pl-4'
          onClick={handleMenuItemClick(setDownloadingAsPdfStatement)}
        >
          <Typography>Export as statement</Typography>
        </MenuItem>

        {connectedIntegrations.includes(INTEGRATION_PROVIDERS.stripe) && (
          <MenuItem
            className='pl-4'
            onClick={handleMenuItemClick(handleSyncToStripe)}
          >
            <Typography>Sync to Stripe</Typography>
          </MenuItem>
        )}

        {isNotNullOrUndefined(invoice) && (
          <ShareInvoicesDialog invoicesData={[{
            invoiceId,
            customerName: invoice.customer.name,
            contactEmail: isNotEmptyArray(invoice.customer.contacts) ? getPrimaryContact(invoice.customer.contacts).email : '---',
            issueDate: invoice.billingCycle.startTime,
            amount: invoice.revenueBreakdown.total,
            currency: invoice.currency,
          }]}
          >
            <MenuItem
              disabled={isNullOrUndefined(invoice.customer.contacts) || isEmptyArray(invoice.customer.contacts)}
              className='pl-4'
            >
              <Typography>Share via email</Typography>
            </MenuItem>
          </ShareInvoicesDialog>
        )}

        <MenuItem onClick={handleDownloadPdf}>
          Download
        </MenuItem>
      </Menu>

      {isDownloadingPdfStatement && (
        <LightInvoicesPdfDownloader
          onFinishedDownloading={() => unsetDownloadingAsPdfStatement()}
          invoicesIds={[invoiceId]}
          isStatement
        />
      )}
    </>
  );
};